import axios from "axios";
import { API_BASE_URL } from "./utils";

const API_URL = API_BASE_URL;

const companyAdminApi = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

companyAdminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("company_admin_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

companyAdminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("company_admin_token");
      localStorage.removeItem("company_admin_company");
      localStorage.removeItem("company_admin_user");
      if (window.location.pathname.startsWith("/admin")) {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(error);
  },
);

const ADMIN_COMPANY = "/admin/company";

export const companyAdminAuthAPI = {
  me: () => companyAdminApi.get(`${ADMIN_COMPANY}/me`),
};

export const expiredFoodsAPI = {
  get: () => companyAdminApi.get("/admin/expired-foods"),
};

export const aiHealthAPI = {
  getHealth: (date) =>
    companyAdminApi.get("/admin/ai-health" + (date ? `?date=${date}` : "")),
};

export const ticketsAPI = {
  list: (params) => companyAdminApi.get(`${ADMIN_COMPANY}/tickets`, { params }),
  count: () => companyAdminApi.get(`${ADMIN_COMPANY}/tickets/count`),
  stats: () => companyAdminApi.get(`${ADMIN_COMPANY}/tickets/stats`),
  getOne: (id) => companyAdminApi.get(`${ADMIN_COMPANY}/tickets/${id}`),
  create: (data) => companyAdminApi.post(`${ADMIN_COMPANY}/tickets`, data),
  update: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/tickets/${id}`, data),
  close: (id) => companyAdminApi.put(`${ADMIN_COMPANY}/tickets/${id}/close`),
  reopen: (id) => companyAdminApi.put(`${ADMIN_COMPANY}/tickets/${id}/reopen`),
  addNote: (id, data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/tickets/${id}/notes`, data),
};

export const companyAdminAppInstallationsAPI = {
  getAll: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/app-installations`, { params }),
  getOne: (id) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/app-installations/${id}`),
  getOverviewStats: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/app-installations/overview-stats`),
};

export const companyAdminRecipesAPI = {
  getAll: (params) => companyAdminApi.get(`${ADMIN_COMPANY}/recipes`, { params }),
  getOne: (id) => companyAdminApi.get(`${ADMIN_COMPANY}/recipes/${id}`),
  create: (data) => companyAdminApi.post(`${ADMIN_COMPANY}/recipes`, data),
  update: (id, data) => companyAdminApi.put(`${ADMIN_COMPANY}/recipes/${id}`, data),
  delete: (id) => companyAdminApi.delete(`${ADMIN_COMPANY}/recipes/${id}`),
  generateDetails: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/recipes/generate`, data),
};

export const companyAdminCustomersAPI = {
  getAll: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/customers`, { params }),
  create: (data) => companyAdminApi.post(`${ADMIN_COMPANY}/customers`, data),
  getOne: (id) => companyAdminApi.get(`${ADMIN_COMPANY}/customers/${id}`),
  updateBasic: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/customers/${id}/basic`, data),
  uploadProfilePhoto: (id, formData) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/customers/${id}/profile-photo`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),
  updateSurvey: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/customers/${id}/survey`, data),
  delete: (id, hard = false) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/customers/${id}`, {
      params: { hard },
    }),
  getHistory: (id, date) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/customers/${id}/history`, {
      params: { date },
    }),
  saveHistoryNote: (id, data) =>
    companyAdminApi.put(
      `${ADMIN_COMPANY}/customers/${id}/history/note`,
      data,
    ),
  getWeekly: (id, date) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/customers/${id}/weekly`, {
      params: { date },
    }),
  getAiUsage: (id) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/customers/${id}/ai-usage`),
  getCustomersOverviewStats: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/customers/overview-stats`, {
      params,
    }),
  exportCustomers: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/customers/export`, { params }),
  bulkMessage: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/customers/bulk-message`, data),
  getPartnersList: () => companyAdminApi.get(`${ADMIN_COMPANY}/partners`),
  getMealTimes: () => companyAdminApi.get(`${ADMIN_COMPANY}/meal-times`),
  foodbankSearch: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/foodbank/search`, { params }),
  foodbankDetail: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/foodbank/detail`, { params }),
  aiFoodBankSearch: (params) =>
    companyAdminApi.get("/ai-food-bank/search", { params }),
  usdaSearch: (params) => companyAdminApi.get("/admin/usda/search", { params }),
  getAiFoodBankStatus: () => companyAdminApi.get("/ai-food-bank/ai-status"),
  strictSummary: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/strict-diet-plans/summary`, {
      params,
    }),
  moderateSummary: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/moderate-diet-plans/summary`, {
      params,
    }),
  strictMealStatus: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/strict-diet-plans/meal-status`, {
      params,
    }),
  strictList: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/strict-diet-plans`, { params }),
  dietPlanAiPreview: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/diet-plan-ai-preview`, data),
  strictCreate: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/strict-diet-plans`, data),
  strictCreateFromAi: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/strict-diet-plans/ai`, data),
  strictRecipeDetails: (data) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/strict-diet-plans/recipe/details`,
      data,
    ),
  strictCreateFromRecipe: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/strict-diet-plans/recipe`, data),
  strictUpdate: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/strict-diet-plans/${id}`, data),
  strictCopy: (id, data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/strict-diet-plans/${id}/copy`, data),
  strictDelete: (id) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/strict-diet-plans/${id}`),
  /** Moderate / Flexible meal foods (Food + meal_time, like mobile). */
  mealFoodList: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/customer-meal-foods`, { params }),
  mealFoodCreate: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/customer-meal-foods`, data),
  mealFoodCreateFromAi: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/customer-meal-foods/ai`, data),
  mealFoodCopy: (id, data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/customer-meal-foods/${id}/copy`, data),
  mealFoodUpdate: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/customer-meal-foods/${id}`, data),
  mealFoodDelete: (id) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/customer-meal-foods/${id}`),

  moderateMealStatus: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/moderate-diet-plans/meal-status`, {
      params,
    }),
  moderateList: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/moderate-diet-plans`, { params }),
  moderateCreate: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/moderate-diet-plans`, data),
  moderateUpdate: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/moderate-diet-plans/${id}`, data),
  moderateDelete: (id, hard = false) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/moderate-diet-plans/${id}`, {
      params: { hard },
    }),

  relaxSummary: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/moderate-relax/summary`, { params }),
  relaxMealStatus: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/moderate-relax/meal-status`, {
      params,
    }),
  relaxList: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/moderate-relax`, { params }),
  relaxCreate: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/moderate-relax`, data),
  relaxUpdate: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/moderate-relax/${id}`, data),
  relaxDelete: (id, hard = false) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/moderate-relax/${id}`, {
      params: { hard },
    }),

  waterGet: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/water`, { params }),
  waterCreate: (data) => companyAdminApi.post(`${ADMIN_COMPANY}/water`, data),
  waterUpdate: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/water/${id}`, data),
  waterDelete: (id) => companyAdminApi.delete(`${ADMIN_COMPANY}/water/${id}`),
  supplementsList: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/supplements`, { params }),
  supplementsCreate: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/supplements`, data),
  supplementsUpdate: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/supplements/${id}`, data),
  supplementsDelete: (id) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/supplements/${id}`),

  labReportsList: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/lab-reports`, { params }),
  labReportsUpload: (formData) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/lab-reports`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  labReportsUpdate: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/lab-reports/${id}`, data),
  labReportsDelete: (id) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/lab-reports/${id}`),

  dietPlanVersionsList: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/diet-plan-versions`, { params }),
  dietPlanVersionsCreate: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/diet-plan-versions`, data),
  dietPlanVersionsGet: (id) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/diet-plan-versions/${id}`),
  dietPlanVersionsCompare: (id) =>
    companyAdminApi.get(
      `${ADMIN_COMPANY}/diet-plan-versions/${id}/compare`,
    ),

  mealNotesList: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/meal-notes`, { params }),
  mealNotesUpsert: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/meal-notes`, data),
  mealPlanPdf: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/meal-plan-pdf`, {
      params,
      responseType: "blob",
    }),

  weightHistory: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/weight-history`, { params }),
  bodyMeasurementsList: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/body-measurements`, { params }),
  bodyMeasurementsTargets: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/body-measurements/targets`, data),
  progressReportPdf: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/progress-report-pdf`, {
      params,
      responseType: "blob",
    }),
  waterIntakeHistory: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/water-intake`, { params }),
  dietIntakeHistory: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/diet-intake/history`, { params }),
  dietIntakeSummary: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/diet-intake/summary`, { params }),

  listGroups: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/groups`, { params }),
  listChatGroups: () => companyAdminApi.get(`${ADMIN_COMPANY}/chat/groups`),
  createGroup: (data) => companyAdminApi.post(`${ADMIN_COMPANY}/groups`, data),
  updateGroup: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/groups/${id}`, data),
  deleteGroup: (id, params) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/groups/${id}`, { params }),
  listGroupUsers: (id) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/groups/${id}/users`),

  listCompanyUsers: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/users`, { params }),
  createCompanyUser: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/users`, data),
  updateCompanyUser: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/users/${id}`, data),
  getUserPermissions: (id) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/users/${id}/permissions`),
  updateUserPermissions: (id, permissions) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/users/${id}/permissions`, {
      permissions,
    }),
  deleteCompanyUser: (id) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/users/${id}`),

  updateMyProfile: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/me/profile`, data),
  changeMyPassword: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/me/password`, data),

  updateCompanyProfile: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/company/profile`, data),
  changeCompanyPassword: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/company/password`, data),

  // Dashboard
  getDashboard: () => companyAdminApi.get(`${ADMIN_COMPANY}/dashboard`),

  // Customer-reported food / intake issues (reported_issues)
  getReportedIssues: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/reported-issues`, { params }),

  // Subscriptions Management
  getSubscriptions: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/subscriptions`, { params }),
  getSubscriptionStats: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/subscriptions/stats/overview`),
  getSubscriptionDetail: (customerId) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/subscriptions/${customerId}`),
  updateSubscription: (customerId, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/subscriptions/${customerId}`, data),
  extendSubscription: (customerId, data) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/subscriptions/${customerId}/extend`,
      data,
    ),
  cancelSubscription: (customerId, data) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/subscriptions/${customerId}/cancel`,
      data,
    ),

  // Partners Management
  getPartners: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/partners`, { params }),
  getPartnerStats: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/partners/stats/overview`),
  getPartnerDetail: (partnerId) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/partners/${partnerId}`),
  createPartner: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/partners`, data),
  updatePartner: (partnerId, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/partners/${partnerId}`, data),
  deletePartner: (partnerId) =>
    companyAdminApi.delete(`${ADMIN_COMPANY}/partners/${partnerId}`),
  togglePartnerStatus: (partnerId) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/partners/${partnerId}/toggle-status`,
    ),
  regeneratePartnerCode: (partnerId) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/partners/${partnerId}/regenerate-code`,
    ),
  setPartnerPassword: (partnerId, data) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/partners/${partnerId}/set-password`,
      data,
    ),

  // Partner Withdrawals
  getWithdrawals: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/partners/withdrawals`, { params }),
  getWithdrawalStats: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/partners/withdrawals/stats`),
  getWithdrawalDetail: (withdrawalId) =>
    companyAdminApi.get(
      `${ADMIN_COMPANY}/partners/withdrawals/${withdrawalId}`,
    ),
  createWithdrawal: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/partners/withdrawals`, data),
  approveWithdrawal: (withdrawalId) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/partners/withdrawals/${withdrawalId}/approve`,
    ),
  completeWithdrawal: (withdrawalId, data) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/partners/withdrawals/${withdrawalId}/complete`,
      data,
    ),
  rejectWithdrawal: (withdrawalId, data) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/partners/withdrawals/${withdrawalId}/reject`,
      data,
    ),

  // Payment History
  getPaymentHistory: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/payment-history`, { params }),
  getPaymentHistoryStats: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/payment-history/stats`),
  getActiveTransactions: (params) =>
    companyAdminApi.get(
      `${ADMIN_COMPANY}/payment-history/active-transactions`,
      { params },
    ),
  exportPaymentHistory: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/payment-history/export`, {
      params,
      responseType: "blob",
    }),

  // AI Chat monitor
  getAiChatSessions: (params) =>
    companyAdminApi.get("/admin/ai-chats/sessions", { params }),
  getAiChatSession: (sessionId, params) =>
    companyAdminApi.get(`/admin/ai-chats/sessions/${sessionId}`, { params }),
  getAiChatStats: () => companyAdminApi.get("/admin/ai-chats/stats"),

  // Settings - Branding
  getBrandingSettings: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/branding`),
  updateBrandingSettings: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/settings/branding`, data),
  uploadBrandingImage: (formData) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/settings/branding/upload`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),

  // Settings - URLs
  getUrlSettings: () => companyAdminApi.get(`${ADMIN_COMPANY}/settings/urls`),
  updateUrlSettings: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/settings/urls`, data),
  getAiAppRatingSettings: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/ai-app-rating`),
  updateAiAppRatingSettings: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/settings/ai-app-rating`, data),

  // Settings - Login Authenticator
  getLoginAuthSettings: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/login-auth`),
  updateLoginAuthSettings: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/settings/login-auth`, data),

  // Settings - Security
  getSecuritySettings: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/security`),
  updateSecuritySettings: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/settings/security`, data),

  // Settings - CAPTCHA
  getCaptchaSettings: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/captcha`),
  updateCaptchaSettings: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/settings/captcha`, data),

  // Settings - FTP
  getFtpSettings: () => companyAdminApi.get(`${ADMIN_COMPANY}/settings/ftp`),
  updateFtpSettings: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/settings/ftp`, data),
  testFtpConnection: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/settings/ftp/test`, data),

  // Settings - Schedulers
  getSchedulers: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/schedulers`),
  updateScheduler: (schedulerId, data) =>
    companyAdminApi.put(
      `${ADMIN_COMPANY}/settings/schedulers/${schedulerId}`,
      data,
    ),
  toggleScheduler: (schedulerId) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/settings/schedulers/${schedulerId}/toggle`,
    ),
  runScheduler: (schedulerId) =>
    companyAdminApi.post(
      `${ADMIN_COMPANY}/settings/schedulers/${schedulerId}/run`,
    ),
  getSchedulerLogs: (schedulerId, params) =>
    companyAdminApi.get(
      `${ADMIN_COMPANY}/settings/schedulers/${schedulerId}/logs`,
      { params },
    ),
  getRecentSchedulerLogs: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/schedulers/logs/recent`, {
      params,
    }),

  // Settings - Mailer / SMTP
  getSmtpSettings: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/mailer/smtp`),
  updateSmtpSettings: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/settings/mailer/smtp`, data),
  testSmtpConnection: (data) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/settings/mailer/smtp/test`, data),

  // Settings - Email Templates
  getEmailTemplates: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/mailer/templates`),
  updateEmailTemplate: (id, data) =>
    companyAdminApi.put(
      `${ADMIN_COMPANY}/settings/mailer/templates/${id}`,
      data,
    ),

  // Settings - Email Logs
  getEmailLogs: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/settings/mailer/logs`, { params }),

  // AI Configuration
  getAiConfig: () => companyAdminApi.get("/admin/ai-config/"),
  updateAiModel: (id, data) =>
    companyAdminApi.put("/admin/ai-config/ai-models/" + id, data),
  updateSharedPrompts: (data) =>
    companyAdminApi.put("/admin/ai-config/shared-prompts", data),
  updateTtsService: (id, data) =>
    companyAdminApi.put("/admin/ai-config/tts-services/" + id, data),
  updateSttService: (id, data) =>
    companyAdminApi.put("/admin/ai-config/stt-services/" + id, data),

  // Gemma 4 Servers
  createGemmaServer: (data) =>
    companyAdminApi.post("/admin/ai-config/gemma-servers", data),
  updateGemmaServer: (id, data) =>
    companyAdminApi.put("/admin/ai-config/gemma-servers/" + id, data),
  deleteGemmaServer: (id) =>
    companyAdminApi.delete("/admin/ai-config/gemma-servers/" + id),

  // Events (Automated Messaging)
  listEvents: (params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/events`, { params }),
  createEvent: (data) => companyAdminApi.post(`${ADMIN_COMPANY}/events`, data),
  updateEvent: (id, data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/events/${id}`, data),
  deleteEvent: (id) => companyAdminApi.delete(`${ADMIN_COMPANY}/events/${id}`),
  getEventLogs: (id, params) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/events/${id}/logs`, { params }),
  // Events Analytics (Phase 2)
  getEventConfig: () => companyAdminApi.get(`${ADMIN_COMPANY}/events/config`),
  updateEventConfig: (data) =>
    companyAdminApi.put(`${ADMIN_COMPANY}/events/config`, data),
  getEventsStats: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/events/stats/overview`),
  getEventAnalytics: (id) =>
    companyAdminApi.get(`${ADMIN_COMPANY}/events/${id}/stats`),
  getQueueStatus: () =>
    companyAdminApi.get(`${ADMIN_COMPANY}/events/queue/status`),
  dryRunEvent: (id) =>
    companyAdminApi.post(`${ADMIN_COMPANY}/events/${id}/dry-run`),
};

export { companyAdminApi };
export default companyAdminApi;
